/**
 * AI 영상 클립(Veo) 캐시 — IndexedDB
 *
 * Veo 는 클립 하나에 $0.2~3 가 든다. 모달을 닫았다 열거나 새로고침했다고 같은 클립을 다시 사는 일이 없도록,
 * "같은 사진 + 같은 지시 + 같은 등급·비율·길이" 로 만든 결과는 브라우저에 두고 다시 쓴다.
 * 키는 그 재료를 이어 붙인 문자열이고, 값은 mp4 Blob 이다. 14일 지난 항목은 열 때 지운다.
 * (Google 쪽 결과 주소는 2일이면 사라지므로 서버가 아니라 브라우저가 들고 있어야 한다.)
 *
 * IndexedDB 를 못 쓰는 환경(사생활 보호 모드 등)에서는 조용히 캐시 없이 동작한다.
 */

const DB_NAME = 'trp-ai-video-clips';
const STORE = 'clips';
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') { resolve(null); return; }
    let req;
    try { req = indexedDB.open(DB_NAME, 1); } catch { resolve(null); return; }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' }).createIndex('at', 'at');
    };
    req.onsuccess = () => { resolve(req.result); pruneOld(req.result); };
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
  return dbPromise;
}

/** 오래된 클립을 지운다 (열 때 한 번). 실패해도 무시. */
function pruneOld(db) {
  try {
    const tx = db.transaction(STORE, 'readwrite');
    const idx = tx.objectStore(STORE).index('at');
    const range = IDBKeyRange.upperBound(Date.now() - MAX_AGE_MS);
    idx.openCursor(range).onsuccess = (e) => {
      const cur = e.target.result;
      if (!cur) return;
      cur.delete();
      cur.continue();
    };
  } catch { /* 무시 */ }
}

/** 캐시 키. 재료가 하나라도 다르면 다른 클립이다. */
export function clipCacheKey({ tier, aspectRatio, durationSeconds, mode = 'photo', imageUrl, referenceUrl, description, prompt, character, heroImageName }) {
  // prompt: 사용자가 확인 창에서 고친 최종 프롬프트. 있으면 그것이 곧 재료다. character/heroImageName: 예능형 주인공(시트·사진)
  return [tier, aspectRatio, durationSeconds, mode, imageUrl || '-', referenceUrl || '-', String(character || '').trim() || '-', heroImageName || '-', String(prompt || '').trim() || String(description || '').trim()].join('|');
}

/** @returns {Promise<{ blob:Blob, seconds:number }|null>} */
export async function getCachedClip(key) {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(key);
      req.onsuccess = () => {
        const row = req.result;
        resolve(row && row.blob && row.blob.size ? { blob: row.blob, seconds: Number(row.seconds) || 0 } : null);
      };
      req.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
}

export async function putCachedClip(key, blob, { seconds } = {}) {
  const db = await openDb();
  if (!db || !blob || !blob.size) return;
  await new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put({ key, blob, seconds: Number(seconds) || 0, at: Date.now() });
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch { resolve(); }
  });
}
